import { useRef, useState } from "react";
import type { CareQueueItem } from "@/api/careQueue";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/button";

export function CompleteCareDialog({ item, contextName = "Main Church", onComplete, onClose }: {
  item: CareQueueItem;
  contextName?: string;
  onComplete: (comment: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [comment, setComment] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const close = () => { if (!submitting.current) onClose(); };
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting.current) return;
    const trimmed = comment.trim();
    if (!trimmed) { setError("Document what was done before completing this follow-up."); return; }
    if (trimmed.length > 4000) { setError("Use 4000 characters or fewer."); return; }
    submitting.current = true;
    setPending(true);
    setError("");
    try { await onComplete(trimmed); onClose(); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to complete follow-up. Please try again."); }
    finally { submitting.current = false; setPending(false); }
  }
  return <Modal open onClose={close} ariaLabelledBy="complete-care-title" panelClassName="max-w-xl">
    <h2 id="complete-care-title" className="text-xl font-semibold">Complete Follow-up</h2>
    <p className="mt-2 text-sm text-slate-400">{contextName}</p>
    <p className="mt-2 font-semibold">{item.member_name || item.household_name || (item.member_id ? "Member follow-up" : item.household_id ? "Household follow-up" : "Ministry follow-up")}</p>
    <p className="mt-1 text-slate-300">{item.title}</p>
    <form onSubmit={submit} className="mt-5 space-y-4">
      <label htmlFor="completion-comment" className="block font-medium">What was done? *</label>
      <textarea id="completion-comment" required maxLength={4000} rows={5} value={comment} disabled={pending}
        aria-describedby={error ? "completion-help completion-error" : "completion-help"} aria-invalid={!!error}
        onChange={event => setComment(event.target.value)}
        className="w-full rounded-xl border border-slate-600 bg-slate-900 p-3 text-white" />
      <p id="completion-help" className="text-sm text-slate-400">Document the contact, action taken, or outcome of this follow-up. Maximum 4000 characters.</p>
      {error && <p id="completion-error" role="alert" className="text-sm text-red-300">{error}</p>}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={close} disabled={pending}>Cancel</Button>
        <Button type="submit" disabled={pending}>{pending ? "Completing�" : "Complete Follow-up"}</Button>
      </div>
    </form>
  </Modal>;
}
